import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";
import { format, isSameDay, parseISO } from "date-fns";

import {
  Avatar,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const OPEN_LANE = "open";

const useStyles = makeStyles((theme) => ({
  board: {
    flex: 1,
    display: "flex",
    gap: theme.spacing(2),
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  lane: {
    flex: "0 0 290px",
    display: "flex",
    flexDirection: "column",
    maxHeight: "100%",
    borderRadius: theme.borderRadius.medium,
    backgroundColor: theme.palette.tintedBackground,
    border: theme.border.divider,
    borderTopWidth: 4,
    transition: "background-color 120ms",
  },
  laneOver: {
    backgroundColor: theme.palette.action.selected,
  },
  laneHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
  },
  laneTitle: {
    fontWeight: 600,
  },
  laneCount: {
    minWidth: 24,
    padding: theme.spacing(0, 1),
    borderRadius: theme.borderRadius.pill,
    backgroundColor: theme.palette.background.paper,
    textAlign: "center",
    ...theme.typography.caption,
    fontWeight: 600,
  },
  laneBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(0, 1, 1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    ...theme.typography.body2,
  },
  card: {
    position: "relative",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.borderRadius.small,
    cursor: "grab",
    overflow: "hidden",
    "&:hover": {
      boxShadow: theme.elevation.light,
    },
  },
  cardDragging: {
    opacity: 0.4,
  },
  queueBar: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    width: 4,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: 14,
  },
  cardName: {
    flex: 1,
    minWidth: 0,
    fontWeight: 500,
  },
  cardTime: {
    color: theme.palette.text.secondary,
    ...theme.typography.caption,
  },
  lastMessage: {
    margin: theme.spacing(0.75, 0),
    color: theme.palette.text.secondary,
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
  },
  pill: {
    padding: "0 6px",
    borderRadius: theme.borderRadius.pill,
    fontSize: 11,
    lineHeight: "18px",
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.secondary,
  },
  pendingPill: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.common.white,
  },
  unread: {
    marginLeft: "auto",
    minWidth: 20,
    padding: "0 6px",
    borderRadius: theme.borderRadius.pill,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    textAlign: "center",
    fontSize: 11,
    lineHeight: "18px",
    fontWeight: 600,
  },
  hint: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

const laneOf = (ticket) => {
  const kanbanTag = ticket.tags?.find((tag) => tag.kanban);
  return kanbanTag ? kanbanTag.id : OPEN_LANE;
};

const TicketCard = ({ ticket, onOpen }) => {
  const classes = useStyles();
  const [dragging, setDragging] = useState(false);
  const updatedAt = parseISO(ticket.updatedAt);

  return (
    <Paper
      className={clsx(classes.card, dragging && classes.cardDragging)}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(ticket.id));
        e.dataTransfer.effectAllowed = "move";
        setDragging(true);
      }}
      onDragEnd={() => setDragging(false)}
      onClick={() => onOpen(ticket.id)}
    >
      <span
        className={classes.queueBar}
        style={{ backgroundColor: ticket.queue?.color || "transparent" }}
      />
      <div className={classes.cardHeader}>
        <Avatar src={ticket.contact?.profilePicUrl} className={classes.avatar}>
          {ticket.contact?.name?.charAt(0)}
        </Avatar>
        <Typography noWrap variant="body2" className={classes.cardName}>
          {ticket.contact?.name}
        </Typography>
        <span className={classes.cardTime}>
          {format(updatedAt, isSameDay(updatedAt, new Date()) ? "HH:mm" : "dd/MM")}
        </span>
      </div>
      <Typography noWrap variant="body2" className={classes.lastMessage}>
        {ticket.lastMessage || " "}
      </Typography>
      <div className={classes.cardFooter}>
        {ticket.status === "pending" && (
          <span className={clsx(classes.pill, classes.pendingPill)}>
            {i18n.t("kanban.pending")}
          </span>
        )}
        <span className={classes.pill}>
          {ticket.user?.name || i18n.t("kanban.unassigned")}
        </span>
        {ticket.queue && <span className={classes.pill}>{ticket.queue.name}</span>}
        {ticket.unreadMessages > 0 && (
          <span className={classes.unread}>
            {ticket.unreadMessages > 99 ? "99+" : ticket.unreadMessages}
          </span>
        )}
      </div>
    </Paper>
  );
};

const Lane = ({ id, title, color, tickets, onDropTicket, onOpen }) => {
  const classes = useStyles();
  const [over, setOver] = useState(false);

  return (
    <div
      className={clsx(classes.lane, over && classes.laneOver)}
      style={{ borderTopColor: color }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!over) setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const ticketId = Number(e.dataTransfer.getData("text/plain"));
        if (ticketId) onDropTicket(ticketId, id);
      }}
    >
      <div className={classes.laneHeader}>
        <Typography variant="subtitle2" className={classes.laneTitle}>
          {title}
        </Typography>
        <span className={classes.laneCount}>{tickets.length}</span>
      </div>
      <div className={classes.laneBody}>
        {tickets.length === 0 && (
          <div className={classes.empty}>{i18n.t("kanban.empty")}</div>
        )}
        {tickets.map((ticket) => (
          <TicketCard key={ticket.id} ticket={ticket} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
};

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const [lanes, setLanes] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const refreshTimer = useRef(null);

  const fetchBoard = useCallback(async () => {
    try {
      const { data } = await api.get("/kanban");
      setLanes(data.lanes);
      setTickets(data.tickets);
    } catch (err) {
      toastError(err);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  useEffect(() => {
    const socket = openSocket();
    // Agrupa rajadas de eventos em uma única atualização
    const scheduleRefresh = () => {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(fetchBoard, 800);
    };

    socket.on("connect", () => socket.emit("joinNotification"));
    socket.on("ticket", scheduleRefresh);
    socket.on("appMessage", scheduleRefresh);
    socket.on("tag", scheduleRefresh);

    return () => {
      clearTimeout(refreshTimer.current);
      socket.disconnect();
    };
  }, [fetchBoard]);

  const handleDropTicket = async (ticketId, laneId) => {
    const ticket = tickets.find((t) => t.id === ticketId);
    if (!ticket || laneOf(ticket) === laneId) return;

    const targetTag = lanes.find((lane) => lane.id === laneId);
    const previous = tickets;

    // Atualização otimista: troca a tag de Kanban do card localmente
    setTickets((prev) =>
      prev.map((t) =>
        t.id !== ticketId
          ? t
          : {
              ...t,
              tags: [
                ...(t.tags || []).filter((tag) => !tag.kanban),
                ...(targetTag ? [targetTag] : []),
              ],
            }
      )
    );

    try {
      await api.put(`/tickets/${ticketId}/kanban`, {
        tagId: laneId === OPEN_LANE ? null : laneId,
      });
    } catch (err) {
      setTickets(previous);
      toastError(err);
    }
  };

  const handleOpen = (ticketId) => history.push(`/tickets/${ticketId}`);

  const columns = [
    { id: OPEN_LANE, title: i18n.t("kanban.open"), color: "#9CA3AF" },
    ...lanes.map((lane) => ({ id: lane.id, title: lane.name, color: lane.color })),
  ];

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("kanban.title")}</Title>
      </MainHeader>
      {loaded && lanes.length === 0 && (
        <Typography variant="body2" className={classes.hint}>
          {i18n.t("kanban.noLanes")}
        </Typography>
      )}
      <div className={classes.board}>
        {columns.map((column) => (
          <Lane
            key={column.id}
            id={column.id}
            title={column.title}
            color={column.color}
            tickets={tickets.filter((ticket) => laneOf(ticket) === column.id)}
            onDropTicket={handleDropTicket}
            onOpen={handleOpen}
          />
        ))}
      </div>
    </MainContainer>
  );
};

export default Kanban;
